import { useState, useMemo, useEffect } from "react";
import { SchemaFormRenderer } from "@/components/form-builder/SchemaFormRenderer";
import type { FormSchema } from "@shared/form-builder-types";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Calendar, Phone, Clock, CheckCircle2, Loader2, MapPin, ChevronRight, ChevronLeft, Sparkles, Check, ChevronsUpDown } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarInitials, getAvatarSource } from "@/lib/avatar";
import type { EventOffering, Region, ScheduleEntry, Talent } from "@shared/schema";

interface PublicTalent {
  id: string;
  name: string;
  bookingSlug: string | null;
  photoUrl: string | null;
  bio: string | null;
  eventDescription: string | null;
  bookingFormTitle: string | null;
  bookingFormSubtitle: string | null;
  bookingFormDisclaimer: string | null;
  workspaceId?: string; // Returned from API for subsequent requests
  branding?: {
    workspaceName: string | null;
    logoUrl: string | null;
    primaryColor: string | null;
    secondaryColor: string | null;
    accentColor: string | null;
  } | null;
  bookingForm?: {
    id: string;
    name: string;
    slug: string;
    clientTypes: string[] | null;
    regionIds: string[] | null;
    calendarIds: string[] | null;
    openAvailability?: boolean;
    formSchemaJson?: string | null;
  };
}

type RequestType = "event_request" | "call_request" | "waitlist";

interface Step1Data {
  contactName: string;
  email: string;
  phone: string;
  companyName: string;
  cityState: string;
}

interface Step2Data {
  regionId: string;
  requestNewRegion: boolean;
  eventOfferingId: string;
  selectedDate: Date | undefined;
  selectedTime: string;
  venueAddress: string;
  eventDetails: string;
  requestType: RequestType;
}

const TIME_SLOTS = [
  "10:00 AM", "10:30 AM", "11:00 AM",
  "1:00 PM", "1:30 PM", "2:00 PM", "3:00 PM", "3:30 PM",
  "6:00 PM", "6:30 PM", "7:00 PM"
];

// State abbreviation → IANA timezone (mirrors server/calendar.ts)
const STATE_TIMEZONE_MAP: Record<string, string> = {
  CT: 'America/New_York', DE: 'America/New_York', DC: 'America/New_York',
  FL: 'America/New_York', GA: 'America/New_York', IN: 'America/Indiana/Indianapolis',
  KY: 'America/Kentucky/Louisville', ME: 'America/New_York', MD: 'America/New_York',
  MA: 'America/New_York', MI: 'America/Detroit', NH: 'America/New_York',
  NJ: 'America/New_York', NY: 'America/New_York', NC: 'America/New_York',
  OH: 'America/New_York', PA: 'America/New_York', RI: 'America/New_York',
  SC: 'America/New_York', VT: 'America/New_York', VA: 'America/New_York',
  WV: 'America/New_York',
  AL: 'America/Chicago', AR: 'America/Chicago', IL: 'America/Chicago',
  IA: 'America/Chicago', KS: 'America/Chicago', LA: 'America/Chicago',
  MN: 'America/Chicago', MS: 'America/Chicago', MO: 'America/Chicago',
  NE: 'America/Chicago', ND: 'America/Chicago', OK: 'America/Chicago',
  SD: 'America/Chicago', TN: 'America/Chicago', TX: 'America/Chicago',
  WI: 'America/Chicago',
  AZ: 'America/Phoenix', CO: 'America/Denver', ID: 'America/Boise',
  MT: 'America/Denver', NM: 'America/Denver', UT: 'America/Denver',
  WY: 'America/Denver',
  CA: 'America/Los_Angeles', NV: 'America/Los_Angeles', OR: 'America/Los_Angeles',
  WA: 'America/Los_Angeles',
  AK: 'America/Anchorage', HI: 'Pacific/Honolulu',
};

/** Extract a 2-letter state abbreviation from a region name like "Denver, CO" */
function stateFromRegionName(name: string): string | null {
  const m = name.match(/,\s*([A-Z]{2})\s*$/i);
  return m ? m[1].toUpperCase() : null;
}

/** Get the IANA timezone for a region name, defaulting to Central Time */
function timezoneForRegion(regionName: string): string {
  const state = stateFromRegionName(regionName);
  return (state && STATE_TIMEZONE_MAP[state]) || 'America/Chicago';
}

/**
 * Get the UTC offset (in hours) for an IANA timezone on a specific date.
 * Correctly handles DST — e.g., CDT is -5, CST is -6.
 */
function getUTCOffsetHours(dateStr: string, timezone: string): number {
  // Use noon UTC on that date to avoid midnight edge cases
  const ref = new Date(`${dateStr}T12:00:00Z`);
  // Format noon UTC in the target timezone to see what local time it is
  const localStr = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(ref);
  const [h, m] = localStr.split(':').map(Number);
  // ref is at 12:00 UTC. Local time is h:m. offset = local - utc = h - 12 (ignoring minutes for US tzs)
  const localHours = h + m / 60;
  return localHours - 12; // positive = east of UTC, negative = west
}

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

export default function RequestShow() {
  const { toast } = useToast();
  const params = useParams<{ slug?: string; formSlug?: string }>();
  const slug = params.slug;
  const formSlug = params.formSlug;
  
  const [currentStep, setCurrentStep] = useState(1);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const [step1Data, setStep1Data] = useState<Step1Data>({
    contactName: "",
    email: "",
    phone: "",
    companyName: "",
    cityState: "",
  });

  const [step2Data, setStep2Data] = useState<Step2Data>({
    regionId: "",
    requestNewRegion: false,
    eventOfferingId: "",
    selectedDate: undefined,
    selectedTime: "",
    venueAddress: "",
    eventDetails: "",
    requestType: "event_request",
  });

  // Fetch all talent for selection page
  const { data: allTalent = [], isLoading: loadingAllTalent } = useQuery<PublicTalent[]>({
    queryKey: ["/api/public/talent"],
    enabled: !slug,
  });

  // Fetch specific talent when slug is provided (with optional formSlug)
  const talentApiPath = formSlug 
    ? `/api/public/book/${slug}/${formSlug}`
    : `/api/public/book/${slug}`;
  
  const { data: selectedTalent, isLoading: loadingTalent, error: talentError } = useQuery<PublicTalent>({
    queryKey: [talentApiPath],
    enabled: !!slug,
  });

  // Get workspace context from talent (determined by slug lookup)
  const workspaceId = selectedTalent?.workspaceId;

  // Fetch show offerings for specific talent
  const { data: talentOfferings = [], isLoading: loadingOfferings } = useQuery<EventOffering[]>({
    queryKey: ["/api/public/event-offerings", selectedTalent?.id, workspaceId],
    queryFn: async () => {
      const url = workspaceId 
        ? `/api/public/event-offerings/${selectedTalent?.id}?workspaceId=${workspaceId}`
        : `/api/public/event-offerings/${selectedTalent?.id}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch show offerings");
      return response.json();
    },
    enabled: !!selectedTalent?.id,
  });

  const bookingFormId = selectedTalent?.bookingForm?.id || null;
  const { data: regions = [], isLoading: loadingRegions } = useQuery<Region[]>({
    queryKey: ["/api/public/regions", workspaceId, bookingFormId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (workspaceId) params.set("workspaceId", workspaceId);
      if (bookingFormId) params.set("bookingFormId", bookingFormId);
      const qs = params.toString();
      const url = qs ? `/api/public/regions?${qs}` : `/api/public/regions`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch regions");
      return response.json();
    },
    enabled: !!workspaceId,
  });

  const { data: regionSchedule = [], isLoading: loadingSchedule } = useQuery<ScheduleEntry[]>({
    queryKey: ["/api/public/schedule/region", step2Data.regionId, workspaceId],
    queryFn: async () => {
      const url = workspaceId 
        ? `/api/public/schedule/region/${step2Data.regionId}?workspaceId=${workspaceId}`
        : `/api/public/schedule/region/${step2Data.regionId}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch schedule");
      return response.json();
    },
    enabled: !!step2Data.regionId && !!workspaceId,
  });

  // Fetch busy times when a date is selected
  const selectedDateString = step2Data.selectedDate 
    ? step2Data.selectedDate.toISOString().split('T')[0] 
    : null;
    
  // Derive the timezone from the selected region name (e.g. "Denver, CO" → America/Denver)
  const selectedRegion = regions.find(r => r.id === step2Data.regionId);
  const regionTimezone = selectedRegion ? timezoneForRegion(selectedRegion.name) : 'America/Chicago';

  const { data: busyData, isLoading: loadingBusyTimes } = useQuery<{
    date: string;
    calendarIds: string[];
    busyTimes: Array<{ start: string; end: string }>;
  }>({
    queryKey: ["/api/public/calendar/busy", selectedDateString, selectedTalent?.id, workspaceId, regionTimezone],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedTalent?.id) params.append('talentId', selectedTalent.id);
      if (workspaceId) params.append('workspaceId', workspaceId);
      params.append('timezone', regionTimezone);
      const url = `/api/public/calendar/busy/${selectedDateString}?${params.toString()}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch busy times");
      return response.json();
    },
    enabled: !!selectedDateString && !!workspaceId,
  });

  // Filter out busy time slots (including buffer times)
  const availableTimeSlots = useMemo(() => {
    if (!busyData?.busyTimes || busyData.busyTimes.length === 0) {
      return TIME_SLOTS;
    }

    // Get buffer times from selected show offering (default to 30 min before, 60 min after)
    const selectedOffering = talentOfferings.find(o => o.id === step2Data.eventOfferingId);
    const minutesBefore = selectedOffering?.minutesBefore ?? 30;
    const minutesAfter = selectedOffering?.minutesAfter ?? 60;

    // Derive the actual UTC offset for the region's timezone on the selected date.
    // This correctly handles DST (e.g. CDT = -5, CST = -6; MDT = -6, MST = -7).
    const tzOffsetHours = selectedDateString
      ? getUTCOffsetHours(selectedDateString, regionTimezone)
      : -6; // fallback to CST

    return TIME_SLOTS.filter(timeSlot => {
      // Parse the time slot (displayed in the region's local timezone)
      const timeMatch = timeSlot.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (!timeMatch) return true;
      
      let hours = parseInt(timeMatch[1], 10);
      const minutes = parseInt(timeMatch[2], 10);
      const period = timeMatch[3].toUpperCase();
      
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      
      // Convert the displayed local time to UTC for comparison with the freeBusy results
      const [year, month, day] = selectedDateString!.split('-').map(Number);
      
      // local_time = UTC_time + tzOffsetHours  →  UTC_time = local_time - tzOffsetHours
      let utcHours = hours - tzOffsetHours;
      let utcDay = day;
      if (utcHours >= 24) {
        utcHours -= 24;
        utcDay += 1;
      } else if (utcHours < 0) {
        utcHours += 24;
        utcDay -= 1;
      }
      
      const slotStart = new Date(Date.UTC(year, month - 1, utcDay, utcHours, minutes, 0, 0));
      const slotEnd = new Date(slotStart.getTime() + 30 * 60 * 1000); // Add 30 minutes for slot duration
      
      // Check if this slot overlaps with any busy period (including buffer times)
      for (const busy of busyData.busyTimes) {
        const busyStart = new Date(busy.start);
        const busyEnd = new Date(busy.end);
        
        // Extend busy period by buffer times
        // Buffer before: need time to arrive/setup before the event starts
        // Buffer after: need time for teardown/travel after the event ends
        const extendedBusyStart = new Date(busyStart.getTime() - minutesBefore * 60 * 1000);
        const extendedBusyEnd = new Date(busyEnd.getTime() + minutesAfter * 60 * 1000);
        
        // Overlap check with extended busy period
        if (slotStart < extendedBusyEnd && slotEnd > extendedBusyStart) {
          return false;
        }
      }
      
      return true;
    });
  }, [busyData, selectedDateString, talentOfferings, step2Data.eventOfferingId]);

  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [regionPopoverOpen, setRegionPopoverOpen] = useState(false);

  const availableDates = useMemo(() => {
    return regionSchedule
      .filter(entry => entry.date && new Date(entry.date) >= new Date())
      .map(entry => new Date(entry.date))
      .sort((a, b) => a.getTime() - b.getTime());
  }, [regionSchedule]);

  // Task #705 — capture utm_*/gclid/fbclid + mint visitor cookie + ping
  // the server beacon on first paint so the touch is recorded even if
  // the visitor never submits the form.
  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    (async () => {
      const { captureAttributionFromUrl, pingTouch } = await import("@/lib/ads-attribution");
      captureAttributionFromUrl();
      if (!cancelled) await pingTouch(workspaceId);
    })();
    return () => { cancelled = true; };
  }, [workspaceId]);

  // Auto-navigate calendar to first available date when region changes
  useEffect(() => {
    if (availableDates.length > 0) {
      setCalendarMonth(availableDates[0]);
    }
  }, [availableDates]);

  const createSubmissionMutation = useMutation({
    mutationFn: async (data: Step1Data) => {
      const url = workspaceId 
        ? `/api/public/funnel-submissions?workspaceId=${workspaceId}`
        : "/api/public/funnel-submissions";
      const { getStoredAttribution } = await import("@/lib/ads-attribution");
      const attribution = getStoredAttribution();
      const response = await apiRequest("POST", url, { ...data, ...attribution });
      return response.json();
    },
    onSuccess: (data) => {
      setSubmissionId(data.id);
      setCurrentStep(2);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save your information. Please try again.",
        variant: "destructive",
      });
    },
  });

  const completeSubmissionMutation = useMutation({
    mutationFn: async (id: string) => {
      const url = workspaceId 
        ? `/api/public/funnel-submissions/${id}/complete?workspaceId=${workspaceId}`
        : `/api/public/funnel-submissions/${id}/complete`;
      const response = await apiRequest("PATCH", url, {});
      return response.json();
    },
  });

  const submitRequestMutation = useMutation({
    mutationFn: async (data: any) => {
      const url = workspaceId 
        ? `/api/public/event-requests?workspaceId=${workspaceId}`
        : "/api/public/event-requests";
      // Task #705 — merge any captured ad attribution (utm/gclid/fbclid +
      // visitor cookie) into the public submission body so the server
      // can forward it down lead → client → invoice.
      const { getStoredAttribution } = await import("@/lib/ads-attribution");
      const attribution = getStoredAttribution();
      const response = await apiRequest("POST", url, { ...data, ...attribution });
      return response.json();
    },
    onSuccess: async () => {
      if (submissionId) {
        await completeSubmissionMutation.mutateAsync(submissionId);
      }
      setSubmitted(true);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to submit request. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleStep1Submit = () => {
    if (!step1Data.contactName || !step1Data.email) {
      toast({
        title: "Required Fields",
        description: "Please fill in your name and email address.",
        variant: "destructive",
      });
      return;
    }
    createSubmissionMutation.mutate(step1Data);
  };

  const handleFinalSubmit = () => {
    if (step2Data.requestType === "event_request") {
      if (!step2Data.regionId && !step2Data.requestNewRegion) {
        toast({
          title: "Region Required",
          description: "Please select a region or request a new one.",
          variant: "destructive",
        });
        return;
      }
      if (!step2Data.eventOfferingId) {
        toast({
          title: "Show Required",
          description: "Please select an event package.",
          variant: "destructive",
        });
        return;
      }
    }

    const requestData = {
      requestType: step2Data.requestType,
      contactName: step1Data.contactName,
      email: step1Data.email,
      phone: step1Data.phone || null,
      companyName: step1Data.companyName || null,
      cityState: step1Data.cityState || null,
      eventOfferingId: step2Data.eventOfferingId || null,
      preferredDate: step2Data.selectedDate ? step2Data.selectedDate.toISOString() : null,
      eventDetails: step2Data.eventDetails || null,
      venueAddress: step2Data.venueAddress || null,
      preferredCallTime: step2Data.selectedTime || null,
    };

    submitRequestMutation.mutate(requestData);
  };

  const formatPrice = (rate: string | null | undefined) => {
    if (!rate) return "Contact for pricing";
    const num = parseFloat(rate);
    return isNaN(num) ? "Contact for pricing" : `$${num.toLocaleString()}`;
  };

  const isDateAvailable = (date: Date) => {
    return availableDates.some(
      d => d.toDateString() === date.toDateString()
    );
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Request Submitted!</h2>
            <p className="text-muted-foreground mb-4">
              Thank you for your interest in booking {selectedTalent?.name || "our talent"}! 
              We'll review your request and get back to you within 24-48 hours.
            </p>
            <Button 
              onClick={() => {
                setSubmitted(false);
                setCurrentStep(1);
                setSubmissionId(null);
                setStep1Data({ contactName: "", email: "", phone: "", companyName: "", cityState: "" });
                setStep2Data({
                  regionId: "",
                  requestNewRegion: false,
                  eventOfferingId: "",
                  selectedDate: undefined,
                  selectedTime: "",
                  venueAddress: "",
                  eventDetails: "",
                  requestType: "event_request",
                });
              }}
              variant="outline"
              data-testid="button-submit-another"
            >
              Submit Another Request
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show talent selection page if no slug is provided
  if (!slug) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto py-12 px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-3">Book an Event!</h1>
            <p className="text-xl text-muted-foreground">
              Select a Talent...
            </p>
          </div>

          {loadingAllTalent ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : allTalent.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              No talent available at this time.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-8">
              {allTalent.map((talent) => (
                <Link
                  key={talent.id}
                  href={`/request-event/${talent.bookingSlug || talent.id}`}
                  className="group flex flex-col items-center text-center"
                  data-testid={`talent-card-${talent.id}`}
                >
                  <div className="relative mb-4">
                    <Avatar className="w-32 h-32 md:w-40 md:h-40 border-4 border-transparent group-hover:border-primary transition-all duration-200 group-hover:scale-105">
                      <AvatarImage src={getAvatarSource(talent)} alt={talent.name} className="object-cover" />
                      <AvatarFallback className="text-3xl bg-muted">
                        {getAvatarInitials(talent.name)}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <h3 className="font-semibold text-sm md:text-base uppercase tracking-wide group-hover:text-primary transition-colors">
                    {talent.name}
                  </h3>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show loading state while fetching talent
  if (loadingTalent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Show error if talent not found
  if (talentError || !selectedTalent) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-lg mx-auto py-16 px-4 text-center">
          <h1 className="text-2xl font-bold mb-4">Talent Not Found</h1>
          <p className="text-muted-foreground mb-6">
            We couldn't find the talent you're looking for.
          </p>
          <Link href="/request-event">
            <Button>Browse All Talent</Button>
          </Link>
        </div>
      </div>
    );
  }

  // ── Schema-driven form renderer (Task #96) ──────────────────────────────
  // If the form has a formSchemaJson, use the visual builder renderer.
  // Legacy forms (null formSchemaJson) fall through to the original two-step flow.
  const rawSchemaJson = selectedTalent.bookingForm?.formSchemaJson;
  const parsedSchema: FormSchema | null = (() => {
    if (!rawSchemaJson) return null;
    try { return JSON.parse(rawSchemaJson); } catch { return null; }
  })();

  if (parsedSchema) {
    const formId = selectedTalent.bookingForm!.id;
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-2xl mx-auto py-8 px-4">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold" data-testid="text-booking-title">
              {selectedTalent.bookingFormTitle || `Book ${selectedTalent.name}!`}
            </h1>
            {(selectedTalent.bookingFormSubtitle || selectedTalent.eventDescription) && (
              <p className="text-muted-foreground mt-2 italic">
                {selectedTalent.bookingFormSubtitle || selectedTalent.eventDescription}
              </p>
            )}
          </div>
          <SchemaFormRenderer
            schema={parsedSchema}
            formId={formId}
            isSubmitting={submitRequestMutation.isPending}
            onSubmit={async (answers) => {
              const url = workspaceId
                ? `/api/public/event-requests?workspaceId=${workspaceId}`
                : "/api/public/event-requests";
              const { getStoredAttribution } = await import("@/lib/ads-attribution");
              const attribution = getStoredAttribution();
              submitRequestMutation.mutate({ ...answers, ...attribution } as any);
            }}
          />
        </div>
      </div>
    );
  }

  const brandPrimary = selectedTalent.branding?.primaryColor || null;
  const brandAccent = selectedTalent.branding?.accentColor || brandPrimary;
  const brandWorkspaceName = selectedTalent.branding?.workspaceName || null;
  const brandLogoUrl = selectedTalent.branding?.logoUrl || null;
  const accentStripStyle = brandAccent
    ? { backgroundColor: brandAccent }
    : undefined;
  const avatarRingStyle = brandPrimary
    ? { borderColor: `${brandPrimary}33` }
    : undefined;
  const stepOnStyle = brandPrimary
    ? { backgroundColor: brandPrimary, color: "#fff" }
    : undefined;
  const stepLineOnStyle = brandPrimary
    ? { backgroundColor: brandPrimary }
    : undefined;
  const primaryButtonStyle = brandPrimary
    ? { backgroundColor: brandPrimary, borderColor: brandPrimary, color: "#fff" }
    : undefined;

  return (
    <div className="min-h-screen bg-background">
      {accentStripStyle && (
        <div
          className="fixed top-0 left-0 right-0 h-1 z-40 print:hidden"
          style={accentStripStyle}
          data-testid="strip-brand-accent"
        />
      )}
      <div className="max-w-5xl mx-auto py-8 px-4">
        {brandLogoUrl && (
          <div className="flex justify-center mb-6">
            <img
              src={brandLogoUrl}
              alt={brandWorkspaceName || "Logo"}
              className="h-12 w-auto max-w-[220px] object-contain"
              data-testid="img-booking-brand-logo"
            />
          </div>
        )}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Avatar className="w-24 h-24 border-4 border-primary/20" style={avatarRingStyle}>
              <AvatarImage src={getAvatarSource(selectedTalent)} alt={selectedTalent.name} className="object-cover" />
              <AvatarFallback className="text-2xl bg-muted">
                {getAvatarInitials(selectedTalent.name)}
              </AvatarFallback>
            </Avatar>
          </div>
          <h1 className="text-4xl font-bold mb-2" data-testid="text-booking-title">
            {selectedTalent.bookingFormTitle || `Book ${selectedTalent.name}!`}
          </h1>
          {(selectedTalent.bookingFormSubtitle || selectedTalent.eventDescription) && (
            <p className="text-muted-foreground text-lg italic max-w-2xl mx-auto" data-testid="text-booking-subtitle">
              {selectedTalent.bookingFormSubtitle || selectedTalent.eventDescription}
            </p>
          )}
        </div>

        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                currentStep >= 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}
              style={currentStep >= 1 ? stepOnStyle : undefined}
            >
              1
            </div>
            <div
              className={`w-16 h-1 rounded ${currentStep >= 2 ? "bg-primary" : "bg-muted"}`}
              style={currentStep >= 2 ? stepLineOnStyle : undefined}
            />
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                currentStep >= 2 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}
              style={currentStep >= 2 ? stepOnStyle : undefined}
            >
              2
            </div>
          </div>
          <span className="ml-4 text-sm text-muted-foreground">Step {currentStep} of 2</span>
        </div>

        {currentStep === 1 && (
          <Card className="max-w-lg mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="contactName">Your Name *</Label>
                <Input
                  id="contactName"
                  value={step1Data.contactName}
                  onChange={(e) => setStep1Data(prev => ({ ...prev, contactName: e.target.value }))}
                  placeholder="Full name"
                  data-testid="input-contact-name"
                />
              </div>
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={step1Data.email}
                  onChange={(e) => setStep1Data(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={step1Data.phone}
                  onChange={(e) => setStep1Data(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(555) 555-5555"
                  data-testid="input-phone"
                />
              </div>
              <div>
                <Label htmlFor="companyName">Company/Organization</Label>
                <Input
                  id="companyName"
                  value={step1Data.companyName}
                  onChange={(e) => setStep1Data(prev => ({ ...prev, companyName: e.target.value }))}
                  placeholder="Organization name"
                  data-testid="input-company-name"
                />
              </div>
              <div>
                <Label htmlFor="cityState">City, State</Label>
                <Input
                  id="cityState"
                  value={step1Data.cityState}
                  onChange={(e) => setStep1Data(prev => ({ ...prev, cityState: e.target.value }))}
                  placeholder="e.g. Dallas, TX"
                  data-testid="input-city-state"
                />
              </div>

              <div className="pt-4">
                <Button 
                  onClick={handleStep1Submit}
                  className="w-full"
                  style={primaryButtonStyle}
                  disabled={createSubmissionMutation.isPending}
                  data-testid="button-next-step"
                >
                  {createSubmissionMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <Button 
                variant="ghost" 
                onClick={() => setCurrentStep(1)}
                data-testid="button-back"
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            </div>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle>How Can We Help?</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={step2Data.requestType}
                  onValueChange={(value) => setStep2Data(prev => ({ ...prev, requestType: value as RequestType }))}
                  className="grid gap-4 md:grid-cols-3"
                >
                  <Label
                    htmlFor="event_request"
                    className={`flex flex-col items-center gap-2 p-4 border rounded-md cursor-pointer transition-colors ${
                      step2Data.requestType === "event_request" ? "border-primary bg-primary/5" : "hover-elevate"
                    }`}
                  >
                    <RadioGroupItem value="event_request" id="event_request" className="sr-only" />
                    <Calendar className="h-8 w-8 text-primary" />
                    <span className="font-medium">Book a Show</span>
                  </Label>

                  <Label
                    htmlFor="call_request"
                    className={`flex flex-col items-center gap-2 p-4 border rounded-md cursor-pointer transition-colors ${
                      step2Data.requestType === "call_request" ? "border-primary bg-primary/5" : "hover-elevate"
                    }`}
                  >
                    <RadioGroupItem value="call_request" id="call_request" className="sr-only" />
                    <Phone className="h-8 w-8 text-primary" />
                    <span className="font-medium">Request a Call</span>
                  </Label>

                  <Label
                    htmlFor="waitlist"
                    className={`flex flex-col items-center gap-2 p-4 border rounded-md cursor-pointer transition-colors ${
                      step2Data.requestType === "waitlist" ? "border-primary bg-primary/5" : "hover-elevate"
                    }`}
                  >
                    <RadioGroupItem value="waitlist" id="waitlist" className="sr-only" />
                    <Clock className="h-8 w-8 text-primary" />
                    <span className="font-medium">Join Waitlist</span>
                  </Label>
                </RadioGroup>
              </CardContent>
            </Card>

            {step2Data.requestType === "event_request" && (
              <div className="grid lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      {selectedTalent?.bookingForm?.openAvailability ? "Choose Preferred Date" : "Select Date"}
                    </CardTitle>
                    {selectedTalent?.bookingForm?.openAvailability && (
                      <p className="text-sm text-muted-foreground">
                        Dates and times will be confirmed upon booking
                      </p>
                    )}
                  </CardHeader>
                  <CardContent>
                    {selectedTalent?.bookingForm?.openAvailability ? (
                      <CalendarComponent
                        mode="single"
                        selected={step2Data.selectedDate}
                        onSelect={(date) => setStep2Data(prev => ({ ...prev, selectedDate: date, selectedTime: "" }))}
                        disabled={(date) => date < new Date()}
                        className="rounded-md border"
                        month={calendarMonth}
                        onMonthChange={setCalendarMonth}
                      />
                    ) : step2Data.regionId && !loadingSchedule ? (
                      <>
                        {availableDates.length > 0 && (
                          <div className="mb-4">
                            <Label className="text-sm text-muted-foreground mb-2 block">
                              Available dates in this region:
                            </Label>
                            <ScrollArea className="h-20">
                              <div className="flex flex-wrap gap-2">
                                {availableDates.slice(0, 10).map((date, idx) => (
                                  <Badge
                                    key={idx}
                                    variant={step2Data.selectedDate?.toDateString() === date.toDateString() ? "default" : "outline"}
                                    className="cursor-pointer"
                                    onClick={() => setStep2Data(prev => ({ ...prev, selectedDate: date, selectedTime: "" }))}
                                  >
                                    {date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                  </Badge>
                                ))}
                              </div>
                            </ScrollArea>
                          </div>
                        )}
                        <CalendarComponent
                          mode="single"
                          selected={step2Data.selectedDate}
                          onSelect={(date) => setStep2Data(prev => ({ ...prev, selectedDate: date, selectedTime: "" }))}
                          disabled={(date) => date < new Date() || !isDateAvailable(date)}
                          className="rounded-md border"
                          month={calendarMonth}
                          onMonthChange={setCalendarMonth}
                          modifiers={{
                            available: availableDates,
                          }}
                          modifiersStyles={{
                            available: { backgroundColor: "hsl(var(--primary) / 0.1)" },
                          }}
                        />
                      </>
                    ) : step2Data.regionId && loadingSchedule ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin" />
                      </div>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        Please select a region first to see available dates.
                      </div>
                    )}
                  </CardContent>
                </Card>

                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="h-5 w-5" />
                        Select Region
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Please select the region closest to you for available dates. {selectedTalent?.name || "We"} {selectedTalent?.name ? "is" : "are"} often willing to travel depending on availability. You can search by two-digit state (e.g. TX)!
                      </p>
                      {loadingRegions ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                      ) : (
                        <Popover open={regionPopoverOpen} onOpenChange={setRegionPopoverOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={regionPopoverOpen}
                              className="w-full justify-between"
                              disabled={step2Data.requestNewRegion}
                              data-testid="select-region"
                            >
                              {step2Data.regionId
                                ? regions.find(r => r.id === step2Data.regionId)?.name
                                : "Select a region..."}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-full p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Search regions (e.g. TX, Dallas)..." data-testid="input-search-region" />
                              <CommandList>
                                <CommandEmpty>No region found.</CommandEmpty>
                                <CommandGroup>
                                  {regions.map(region => (
                                    <CommandItem
                                      key={region.id}
                                      value={region.name}
                                      onSelect={() => {
                                        setStep2Data(prev => ({ 
                                          ...prev, 
                                          regionId: region.id,
                                          selectedDate: undefined,
                                          requestNewRegion: false
                                        }));
                                        setRegionPopoverOpen(false);
                                      }}
                                      data-testid={`region-option-${region.id}`}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          step2Data.regionId === region.id ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      {region.name}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      )}
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="requestNewRegion"
                          checked={step2Data.requestNewRegion}
                          onCheckedChange={(checked) => setStep2Data(prev => ({ 
                            ...prev, 
                            requestNewRegion: checked as boolean,
                            regionId: checked ? "" : prev.regionId
                          }))}
                        />
                        <Label htmlFor="requestNewRegion" className="text-sm">
                          My region is not available - request a new region
                        </Label>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Select Show</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {loadingOfferings ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                      ) : (
                        <RadioGroup
                          value={step2Data.eventOfferingId}
                          onValueChange={(value) => setStep2Data(prev => ({ ...prev, eventOfferingId: value }))}
                          className="space-y-3"
                        >
                          {talentOfferings.map((offering) => (
                            <Label
                              key={offering.id}
                              htmlFor={`offering-${offering.id}`}
                              className={`flex items-center justify-between p-4 border rounded-md cursor-pointer transition-colors ${
                                step2Data.eventOfferingId === offering.id
                                  ? "border-primary bg-primary/5"
                                  : "hover-elevate"
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <RadioGroupItem
                                  value={offering.id}
                                  id={`offering-${offering.id}`}
                                />
                                <span className="font-medium">{offering.name}</span>
                                {offering.isDefault && (
                                  <Badge variant="secondary" className="text-xs">Popular</Badge>
                                )}
                              </div>
                              <span className="font-semibold text-primary">
                                {formatPrice(offering.rate)}
                              </span>
                            </Label>
                          ))}
                        </RadioGroup>
                      )}
                      {selectedTalent?.bookingFormDisclaimer && (
                        <p className="text-sm text-muted-foreground mt-3 text-center" data-testid="text-booking-disclaimer">
                          {selectedTalent.bookingFormDisclaimer}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {step2Data.selectedDate && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Select Time</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-3">
                          Times shown in your region's timezone
                        </p>
                        {loadingBusyTimes ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="h-5 w-5 animate-spin mr-2" />
                            <span className="text-sm text-muted-foreground">Checking availability...</span>
                          </div>
                        ) : availableTimeSlots.length === 0 ? (
                          <div className="text-center py-4">
                            <p className="text-muted-foreground">No available time slots for this date.</p>
                            <p className="text-sm text-muted-foreground mt-1">Please select a different date.</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-3 gap-2">
                            {availableTimeSlots.map((time) => (
                              <Button
                                key={time}
                                variant={step2Data.selectedTime === time ? "default" : "outline"}
                                size="sm"
                                onClick={() => setStep2Data(prev => ({ ...prev, selectedTime: time }))}
                                data-testid={`button-time-${time.replace(/[:\s]/g, "-")}`}
                              >
                                {time}
                              </Button>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            )}

            {step2Data.requestType === "event_request" && (
              <Card>
                <CardHeader>
                  <CardTitle>Event Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="venueAddress">Venue Address</Label>
                    <Input
                      id="venueAddress"
                      value={step2Data.venueAddress}
                      onChange={(e) => setStep2Data(prev => ({ ...prev, venueAddress: e.target.value }))}
                      placeholder="Full venue address"
                      data-testid="input-venue-address"
                    />
                  </div>
                  <div>
                    <Label htmlFor="eventDetails">Tell us about your event</Label>
                    <Textarea
                      id="eventDetails"
                      value={step2Data.eventDetails}
                      onChange={(e) => setStep2Data(prev => ({ ...prev, eventDetails: e.target.value }))}
                      placeholder="What's the occasion? Any special requests?"
                      rows={4}
                      data-testid="textarea-event-details"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {step2Data.requestType === "call_request" && (
              <Card>
                <CardHeader>
                  <CardTitle>Call Preferences</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Best time to call</Label>
                    <Select
                      value={step2Data.selectedTime}
                      onValueChange={(value) => setStep2Data(prev => ({ ...prev, selectedTime: value }))}
                    >
                      <SelectTrigger data-testid="select-call-time">
                        <SelectValue placeholder="Select a time preference" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="morning">Morning (9 AM - 12 PM CT)</SelectItem>
                        <SelectItem value="afternoon">Afternoon (12 PM - 5 PM CT)</SelectItem>
                        <SelectItem value="evening">Evening (5 PM - 8 PM CT)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="eventDetails">What would you like to discuss?</Label>
                    <Textarea
                      id="eventDetails"
                      value={step2Data.eventDetails}
                      onChange={(e) => setStep2Data(prev => ({ ...prev, eventDetails: e.target.value }))}
                      placeholder="Tell us about your event or questions..."
                      rows={4}
                      data-testid="textarea-call-details"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {step2Data.requestType === "waitlist" && (
              <Card>
                <CardHeader>
                  <CardTitle>Waitlist Preferences</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Preferred Timeframe</Label>
                    <Select
                      value={step2Data.selectedTime}
                      onValueChange={(value) => setStep2Data(prev => ({ ...prev, selectedTime: value }))}
                    >
                      <SelectTrigger data-testid="select-timeframe">
                        <SelectValue placeholder="Select preferred months" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="spring">Spring (March - May)</SelectItem>
                        <SelectItem value="summer">Summer (June - August)</SelectItem>
                        <SelectItem value="fall">Fall (September - November)</SelectItem>
                        <SelectItem value="winter">Winter (December - February)</SelectItem>
                        <SelectItem value="any">Any time - flexible</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="eventDetails">Additional Information</Label>
                    <Textarea
                      id="eventDetails"
                      value={step2Data.eventDetails}
                      onChange={(e) => setStep2Data(prev => ({ ...prev, eventDetails: e.target.value }))}
                      placeholder="Tell us about the type of event you're planning..."
                      rows={4}
                      data-testid="textarea-waitlist-details"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-end pt-4">
              <Button
                size="lg"
                onClick={handleFinalSubmit}
                disabled={submitRequestMutation.isPending}
                style={primaryButtonStyle}
                data-testid="button-submit-request"
              >
                {submitRequestMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Request"
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
